import { api } from '../careand-redux/api/ApiThunks';
import { store } from '../careand-redux/store';
import { getUnwrappedDispatch, UnwrappedAppDispatch } from '../careand-redux/useAppDispatch';
import { type CareandPrompts, jsonPrompts } from '../generated/prompts';

export type RunSavedPromptFunctionType = typeof runSavedPromptTyped;

export async function runSavedPromptTyped<T extends PromptName>(
  name: T,
  input: PromptInput<CareandPrompts[T]>,
  extra: ExtraArgs<CareandPrompts[T]> & {
    streamingKey?: string;
    budgetTokens?: number;
    validate?: boolean;
  },
): Promise<PromptOutput<CareandPrompts[T]> | undefined> {
  const dispatch = getUnwrappedDispatch(store.dispatch);
  const fields = Object.entries(input).map(([name, value]) => ({ name, value: value as string }));

  const result = await dispatch(
    api.AI.runSavedPrompt({
      name,
      savedPromptRequest: {
        fields,
        patient_id: extra.patientId,
        document_id: extra.documentId,
        template_id: extra.templateId,
        budget_tokens: extra.budgetTokens,
      },
      validate: extra?.validate || false,
      streamingKey: extra.streamingKey,
    }),
  );

  if (!result?.value) {
    return;
  }

  if ((jsonPrompts as Record<string, boolean>)[name]) {
    return JSON.parse(result.value);
  }

  return result.value as any;
}

export type PromptName = keyof CareandPrompts;
type NonBuiltins<T extends string> = T extends `$$$${infer S}` ? never : T;
type OmitBuiltins<T> = keyof T extends string ? { [k in NonBuiltins<keyof T>]: T[k] } : never;

export type PromptInput<S extends CareandPrompts[PromptName]> = OmitBuiltins<S['input']>;
export type PromptOutput<S extends CareandPrompts[PromptName]> = S['output'];
type Eq<A, B> = A extends B ? (B extends A ? true : false) : false;

export type ExtraArgs<S extends CareandPrompts[PromptName]> = (true extends S['requiresPatient']
  ? { patientId: string }
  : { patientId?: string }) &
  (true extends S['requiresDocument'] ? { documentId: string } : { documentId?: string }) &
  (true extends S['requiresTemplate'] ? { templateId: string } : { templateId?: string });
